import { useState, useEffect, useRef, useCallback } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { acquireApiToken } from '@/auth/acquireApiToken';

export type DealStatus = "scheduled" | "active" | "expired" | "deleted";
export type NotificationAudience = "bell" | "checked_in" | "live" | "nearby";

// Canonical short-name weekday set — matches the backend (post-deal / put-deal
// lambdas), the deal-series generator, and the business app.
export type RecurrenceDay =
  | "mon"
  | "tue"
  | "wed"
  | "thu"
  | "fri"
  | "sat"
  | "sun";

export interface Deal {
  id: string;
  business_id: string;
  business_name: string;
  business_logo: string | null;
  business_timezone: string | null;
  title: string;
  description: string | null;
  start_time: string;
  end_time: string;
  notification_audience: NotificationAudience[];
  status: DealStatus;
  created_at: string;
  updated_at: string;
  recurrence_days: RecurrenceDay[] | null;
  recurrence_end_date: string | null;
  parent_deal_id: string | null;
}

interface DealsResponse {
  items: Deal[];
  count: number;
  total: number;
}

export interface UseDealsOptions {
  search?: string;
  page?: number;
  limit?: number;
  apiUrl?: string;
  /** Include materialized child occurrences alongside parents and one-offs. */
  includeOccurrences?: boolean;
  /** Show only the occurrences of this one series, oldest first. */
  parentDealId?: string | null;
}

interface UseDealsResult {
  deals: Deal[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useDeals(opts: UseDealsOptions = {}): UseDealsResult {
  const {
    search = "",
    page = 0,
    limit = 25,
    apiUrl,
    includeOccurrences = false,
    parentDealId = null,
  } = opts;
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;
  const [refreshTick, setRefreshTick] = useState(0);

  const refetch = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    let isMounted = true;

    const fetchDeals = async () => {
      const url = apiUrl || process.env.REACT_APP_API_URL;
      if (!url) {
        if (isMounted) {
          setError("REACT_APP_API_URL is not set");
          setLoading(false);
        }
        return;
      }

      if (isMounted) setLoading(true);

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const offset = page * limit;
        const params = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
        });
        if (search) params.set("search", search);
        if (parentDealId) params.set("parentDealId", parentDealId);
        else if (includeOccurrences) params.set("includeOccurrences", "true");

        const response = await fetch(`${url}/admin/deals?${params}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const data: DealsResponse = await response.json();
        if (!response.ok)
          throw new Error((data as any).error || `HTTP ${response.status}`);

        if (isMounted) {
          setDeals(data.items);
          setTotal(data.total);
          setError(null);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err?.message ?? "Unknown error");
          setDeals([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDeals();
    return () => {
      isMounted = false;
    };
  }, [
    isAuthenticated,
    instance,
    search,
    page,
    limit,
    refreshTick,
    apiUrl,
    includeOccurrences,
    parentDealId,
  ]);

  return { deals, total, loading, error, refetch };
}
